<template>
  <div class="flow-triggers">
    <div class="d-flex justify-content-between align-items-center mb-1">
      <label class="form-label small text-muted mb-0">
        <CygnusIcon name="bolt" class="me-1" />Triggers — enrol on a business signal
      </label>
      <button type="button" class="btn btn-sm btn-outline-primary" @click="openCreate">
        <CygnusIcon name="plus" class="me-1" />Add trigger
      </button>
    </div>
    <p class="text-muted small mb-2">
      When one of the chosen events fires for a prospect matching the filters, enrol
      them in this flow. New rules start in review mode with a cap of 10 a day.
    </p>

    <div v-if="error" class="alert alert-danger py-1 px-2 small mb-2">{{ error }}</div>

    <div v-if="loading" class="text-muted small fst-italic">
      <CygnusIcon name="spinner" :spin="true" class="me-1" />Loading triggers…
    </div>
    <div v-else-if="!rules.length" class="text-muted small fst-italic mb-2">
      No triggers yet — add one to enrol prospects automatically when a signal fires.
    </div>

    <ul v-else class="trigger-list list-unstyled mb-2">
      <li v-for="r in rules" :key="r.id" class="trigger-list__item">
        <div class="trigger-list__main">
          <span class="trigger-list__name">{{ r.name }}</span>
          <span :class="['trigger-pill', r.mode === 'auto' ? 'trigger-pill--auto' : 'trigger-pill--review']">
            {{ r.mode === 'auto' ? 'Automatic' : 'Review first' }}
          </span>
          <span v-if="!r.active" class="trigger-pill trigger-pill--off">Paused</span>
          <span class="trigger-list__cap text-muted">cap {{ r.daily_cap }}/day</span>
        </div>
        <div class="trigger-list__events">
          <span v-for="t in r.event_types" :key="t" class="trigger-evt">{{ labelFor(t) }}</span>
        </div>
        <div class="trigger-list__actions">
          <button type="button" class="btn btn-link btn-sm p-0 me-2" @click="openEdit(r)">Edit</button>
          <button type="button" class="btn btn-link btn-sm p-0 text-danger" @click="remove(r)">Delete</button>
        </div>
      </li>
    </ul>

    <!-- Create / edit modal (inline card) -->
    <div v-if="draft" class="trigger-editor card mb-2">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-start mb-2">
          <h6 class="mb-0">{{ draft.id ? 'Edit trigger' : 'New trigger' }}</h6>
          <button type="button" class="btn btn-link btn-sm p-0" @click="draft = null">Close</button>
        </div>

        <div class="mb-2">
          <label class="form-label small mb-1">Name</label>
          <input v-model="draft.name" type="text" class="form-control form-control-sm" placeholder="e.g. New office movers">
        </div>

        <div class="mb-2">
          <label class="form-label small mb-1">Fire on these events</label>
          <div v-for="g in catalogue" :key="g.key" class="trigger-cat">
            <div class="trigger-cat__head">
              <CygnusIcon :name="g.icon" class="me-1" />{{ g.label }}
            </div>
            <div class="trigger-cat__types">
              <label v-for="t in g.types" :key="t.type" class="trigger-check">
                <input type="checkbox" :value="t.type" v-model="draft.event_types">
                <span>{{ t.label }}</span>
              </label>
            </div>
          </div>
        </div>

        <div class="mb-2">
          <label class="form-label small mb-1">Only prospects matching (all optional)</label>
          <div class="row g-2">
            <div class="col-12">
              <input v-model="draft.filters.sectors" type="text" class="form-control form-control-sm"
                     placeholder="Sectors — SIC codes or letters, comma separated (e.g. 62, J)">
            </div>
            <div class="col-6">
              <input v-model="draft.filters.region" type="text" class="form-control form-control-sm" placeholder="Region">
            </div>
            <div class="col-6">
              <input v-model.number="draft.filters.min_employees" type="number" min="0" class="form-control form-control-sm" placeholder="Min staff">
            </div>
            <div class="col-6">
              <select v-model="draft.filters.registration_status" class="form-select form-select-sm">
                <option value="">Any registration status</option>
                <option value="registered">Registered (Companies House)</option>
                <option value="unregistered">Unregistered</option>
              </select>
            </div>
            <div class="col-6 d-flex align-items-center">
              <label class="trigger-check mb-0">
                <input type="checkbox" v-model="draft.filters.hiring">
                <span>Currently hiring</span>
              </label>
            </div>
          </div>
        </div>

        <div class="mb-2">
          <label class="form-label small mb-1">When a prospect matches</label>
          <label class="trigger-mode">
            <input type="radio" value="review" v-model="draft.mode">
            <span><strong>Review first</strong> — approve each match before it enters the flow</span>
          </label>
          <label class="trigger-mode">
            <input type="radio" value="auto" v-model="draft.mode">
            <span><strong>Automatic</strong> — matches enter the flow directly</span>
          </label>
          <div v-if="draft.mode === 'auto'" class="trigger-auto-note small">
            <CygnusIcon name="warning" class="me-1" />Matches are enrolled without review. The first
            send still respects suppression, unsubscribes and your send window.
          </div>
        </div>

        <div class="mb-2" style="max-width: 220px">
          <label class="form-label small mb-1">Daily cap (matches per day)</label>
          <input v-model.number="draft.daily_cap" type="number" min="1" max="1000" class="form-control form-control-sm">
        </div>

        <div class="d-flex gap-2 align-items-center">
          <button type="button" class="btn btn-sm btn-primary" :disabled="saving || !canSave" @click="save">
            <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="saving" />
            <span v-else>{{ draft.id ? 'Save trigger' : 'Create trigger' }}</span>
          </button>
          <label class="trigger-check mb-0">
            <input type="checkbox" v-model="draft.active">
            <span>Active</span>
          </label>
          <span v-if="saveMsg" class="small text-danger">{{ saveMsg }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";

export default {
  name: "FlowTriggers",
  props: {
    flowId: { type: Number, required: true },
    flowStatus: { type: String, default: "" },
  },
  data() {
    return {
      api: useApi(),
      rules: [],
      catalogue: [],
      draft: null,
      loading: false,
      saving: false,
      error: null,
      saveMsg: "",
    };
  },
  computed: {
    canSave() {
      return !!(this.draft && this.draft.name.trim() && this.draft.event_types.length);
    },
    typeLabels() {
      const map = {};
      for (const g of this.catalogue) for (const t of g.types) map[t.type] = t.label;
      return map;
    },
  },
  created() {
    this.loadCatalogue();
    this.loadRules();
  },
  methods: {
    labelFor(t) {
      return this.typeLabels[t] || t;
    },
    async loadCatalogue() {
      try {
        const res = await this.api.get("/api/v1/admin/triggers/event-catalogue");
        this.catalogue = (res?.data ?? res)?.categories || [];
      } catch (e) {
        this.catalogue = [];
      }
    },
    async loadRules() {
      this.loading = true;
      this.error = null;
      try {
        const res = await this.api.get(`/api/v1/admin/drip-flows/${this.flowId}/triggers`);
        this.rules = (res?.data ?? res)?.data || [];
      } catch (e) {
        this.error = "Could not load triggers.";
      } finally {
        this.loading = false;
      }
    },
    blankDraft() {
      return {
        id: null,
        name: "",
        event_types: [],
        filters: { sectors: "", region: "", min_employees: null, registration_status: "", hiring: false },
        mode: "review",
        daily_cap: 10,
        active: true,
      };
    },
    openCreate() {
      this.saveMsg = "";
      this.draft = this.blankDraft();
    },
    openEdit(r) {
      this.saveMsg = "";
      const f = r.filters || {};
      this.draft = {
        id: r.id,
        name: r.name,
        event_types: [...(r.event_types || [])],
        filters: {
          sectors: Array.isArray(f.sectors) ? f.sectors.join(", ") : (f.sectors || ""),
          region: f.region || "",
          min_employees: f.min_employees || null,
          registration_status: f.registration_status || "",
          hiring: !!f.hiring,
        },
        mode: r.mode || "review",
        daily_cap: r.daily_cap || 10,
        active: r.active !== false,
      };
    },
    cleanFilters() {
      const f = this.draft.filters;
      const out = {};
      const sectors = (f.sectors || "").trim();
      if (sectors) out.sectors = sectors;
      if ((f.region || "").trim()) out.region = f.region.trim();
      if (f.min_employees && Number(f.min_employees) > 0) out.min_employees = Number(f.min_employees);
      if (f.registration_status) out.registration_status = f.registration_status;
      if (f.hiring) out.hiring = true;
      return out;
    },
    async save() {
      if (!this.canSave) return;
      if (this.draft.mode === "auto") {
        const ok = window.confirm(
          "Automatic mode enrols every matching prospect into this flow without review. " +
            "The first send still respects suppression, unsubscribes and your send window. Continue?"
        );
        if (!ok) return;
      }
      this.saving = true;
      this.saveMsg = "";
      const body = {
        name: this.draft.name.trim(),
        event_types: this.draft.event_types,
        filters: this.cleanFilters(),
        mode: this.draft.mode,
        daily_cap: this.draft.daily_cap,
        active: this.draft.active,
      };
      try {
        if (this.draft.id) {
          await this.api.put(`/api/v1/admin/triggers/${this.draft.id}`, body);
        } else {
          await this.api.post(`/api/v1/admin/drip-flows/${this.flowId}/triggers`, body);
        }
        this.draft = null;
        await this.loadRules();
      } catch (e) {
        this.saveMsg = e?.response?.data?.error || "Could not save the trigger.";
      } finally {
        this.saving = false;
      }
    },
    async remove(r) {
      if (!window.confirm(`Delete the trigger "${r.name}"? This does not remove anyone already enrolled.`)) return;
      try {
        await this.api.delete(`/api/v1/admin/triggers/${r.id}`);
        await this.loadRules();
      } catch (e) {
        this.error = "Could not delete the trigger.";
      }
    },
  },
};
</script>

<style scoped>
.flow-triggers { font-size: 0.9rem; }
.trigger-list__item {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem 0.6rem;
  padding: 0.4rem 0;
  border-bottom: 1px solid var(--bs-border-color, #eee);
}
.trigger-list__main { display: flex; align-items: center; gap: 0.4rem; flex: 1 1 auto; }
.trigger-list__name { font-weight: 600; }
.trigger-list__cap { font-size: 0.78rem; }
.trigger-list__events { display: flex; flex-wrap: wrap; gap: 0.25rem; flex: 1 1 100%; }
.trigger-evt {
  font-size: 0.74rem;
  background: var(--bs-tertiary-bg, #f2f2f2);
  border-radius: 0.5rem;
  padding: 0.05rem 0.45rem;
}
.trigger-pill {
  font-size: 0.72rem;
  border-radius: 0.5rem;
  padding: 0.05rem 0.45rem;
  font-weight: 600;
}
.trigger-pill--auto { background: #ffe8cc; color: #9a4d00; }
.trigger-pill--review { background: #e3f0ff; color: #0b5cad; }
.trigger-pill--off { background: #eee; color: #666; }
.trigger-cat { margin-bottom: 0.4rem; }
.trigger-cat__head { font-size: 0.78rem; font-weight: 600; color: var(--bs-secondary-color, #666); margin-bottom: 0.15rem; }
.trigger-cat__types { display: flex; flex-wrap: wrap; gap: 0.4rem 0.9rem; }
.trigger-check { display: inline-flex; align-items: center; gap: 0.3rem; font-size: 0.82rem; }
.trigger-mode { display: flex; align-items: flex-start; gap: 0.4rem; font-size: 0.85rem; margin-bottom: 0.2rem; }
.trigger-auto-note {
  background: #fff6e6;
  border: 1px solid #ffe0a3;
  border-radius: 0.4rem;
  padding: 0.35rem 0.5rem;
  margin-top: 0.25rem;
  color: #7a4a00;
}
</style>
